import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, TablePagination, Typography } from "@mui/material";
import {
  flexRender,
  type Row,
  type RowData,
  type Table,
} from "@tanstack/react-table";

import { colors } from "Infrastructure/Themes/Colors";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { TableSkeleton } from "Shared/DataTable/TableSkeleton";
import { ExpandArrowIcon } from "Shared/Icons";

// Augment ColumnMeta with project-specific styling props.
// Any file that imports BlTanstackTable will pick this up automatically.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    maxWidth?: number;
    fillRow?: boolean;
    justifySelf?: string;
  }
}

const c = colors();

type BlTanstackTableProps<T extends object> = {
  /** Table instance created with useReactTable() in the parent component. */
  table: Table<T>;
  isLoading?: boolean;
  isPaginationDisabled?: boolean;
  /**
   * Total row count for server-side pagination.
   * If omitted, falls back to table.getRowCount() (client-side).
   */
  totalCount?: number;
  skeletonRowsCount?: number;
  onRowClick?: (row: Row<T>) => void;
};

export const BlTanstackTable = <T extends object>({
  table,
  isLoading,
  isPaginationDisabled,
  totalCount,
  skeletonRowsCount,
  onRowClick,
}: BlTanstackTableProps<T>): React.ReactNode => {
  const { t } = useResource();

  const visibleColumns = table.getVisibleLeafColumns();

  const gridTemplateColumns = visibleColumns
    .map((col) => {
      const { maxWidth, fillRow } = col.columnDef.meta ?? {};
      return `minmax(auto, ${fillRow ? "1fr" : maxWidth ? `${maxWidth}px` : "auto"})`;
    })
    .join(" ");

  return (
    <>
      <Box
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns,
          mt: Spacing.S,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: Radius.M,
        })}
      >
        {/* Header row */}
        {table.getHeaderGroups().map((headerGroup) =>
          headerGroup.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const sortDir = header.column.getIsSorted();

            return (
              <Box
                key={header.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: Spacing.S,
                  px: Spacing.M,
                  py: Spacing.M,
                  bgcolor: c.BackgroundTile,
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "common.white",
                  cursor: canSort ? "pointer" : "default",
                  userSelect: "none",
                  justifyContent: header.column.columnDef.meta?.justifySelf,
                  "&:hover": canSort ? { bgcolor: c.BackgroundTileHover } : {},
                }}
                onClick={
                  canSort ? header.column.getToggleSortingHandler() : undefined
                }
              >
                {!header.isPlaceholder &&
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                {canSort && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transition: "transform 0.2s ease",
                      transform:
                        sortDir === "asc" ? "rotate(180deg)" : "rotate(0deg)",
                      opacity: sortDir === false ? 0.3 : 1,
                    }}
                  >
                    <ExpandArrowIcon style={{ width: 12, height: "auto" }} />
                  </Box>
                )}
              </Box>
            );
          }),
        )}

        {/* Data rows */}
        {!isLoading &&
          table.getRowModel().rows.map((row) =>
            row.getVisibleCells().map((cell) => (
              <Box
                key={cell.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: Spacing.M,
                  py: Spacing.M,
                  borderBottom: `1px solid ${c.BorderTable}`,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: onRowClick ? "pointer" : "default",
                  justifyContent: cell.column.columnDef.meta?.justifySelf,
                }}
                onClick={() => onRowClick?.(row)}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Box>
            )),
          )}
      </Box>

      {/* No data */}
      {!isLoading && table.getRowModel().rows.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            borderBottom: `1px solid ${c.BorderTable}`,
            py: 3,
          }}
        >
          <Typography>{t(Resources.Table.NoData)}</Typography>
        </Box>
      )}

      {/* Loading skeleton */}
      {isLoading && <TableSkeleton rowsCount={skeletonRowsCount ?? 5} />}

      {/* Pagination */}
      {!isPaginationDisabled && (
        <TablePagination
          component="div"
          count={totalCount ?? table.getRowCount()}
          page={table.getState().pagination.pageIndex}
          rowsPerPage={table.getState().pagination.pageSize}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onPageChange={(_, newPage) => table.setPageIndex(newPage)}
          onRowsPerPageChange={(e) => table.setPageSize(+e.target.value)}
          labelRowsPerPage={t(Resources.Table.RowsPerPage)}
          labelDisplayedRows={({ count }) => `${count}`}
          backIconButtonProps={{ title: t(Resources.Table.PrevPage) }}
          nextIconButtonProps={{ title: t(Resources.Table.NextPage) }}
          SelectProps={{ IconComponent: ExpandMoreIcon, margin: "dense" }}
          sx={{
            color: c.White,
            borderBottom: "none",
            display: "block",
            ml: "auto",
            "& .MuiTablePagination-spacer": { display: "none" },
            "& .MuiTablePagination-toolbar": { justifyContent: "flex-end" },
            "& .MuiTablePagination-selectLabel": {
              color: c.White,
              fontSize: 14,
            },
            "& .MuiTablePagination-displayedRows": {
              color: c.White,
              fontSize: 14,
            },
            "& .MuiTablePagination-select": { color: c.White, fontSize: 14 },
            "& .MuiSelect-select": { boxShadow: "none" },
            "& .MuiSelect-icon": { color: c.White },
            "& .MuiIconButton-root": { color: c.White },
            "& .MuiIconButton-root.Mui-disabled": {
              color: "rgba(255,255,255,0.3)",
            },
          }}
        />
      )}
    </>
  );
};
